/**
 * Esquema de la base local (SQLite) de cada equipo. Espeja las tablas que bajan por
 * powersync/sync-rules.yaml. PowerSync agrega la columna `id` (texto) a cada tabla.
 * Montos en centavos (integer). Cantidades en real.
 */
import { Schema, Table, column } from "@powersync/web";

const businesses = new Table({
  name: column.text, slug: column.text, logo_path: column.text, accent_color: column.text,
  cuit: column.text, iva_condition: column.text, status: column.text,
  legal_name: column.text, iibb: column.text, activity_start: column.text, fiscal_address: column.text,
  city: column.text, province: column.text, phone: column.text, commercial_address: column.text, reservation_hours: column.integer,
  postal_code: column.text, iibb_condition: column.text, setup_completed_at: column.text, setup_steps: column.text,
  trade_name: column.text, doc_footers: column.text,
});

const locations = new Table({ business_id: column.text, name: column.text, address: column.text, is_active: column.integer, lat: column.real, lng: column.real, trade_name: column.text, created_at: column.text, address_parts: column.text });

const cash_registers = new Table({ business_id: column.text, location_id: column.text, name: column.text, is_active: column.integer, user_id: column.text });

const devices = new Table({
  business_id: column.text, location_id: column.text, cash_register_id: column.text, name: column.text,
  platform: column.text, prefix: column.text, status: column.text, app_version: column.text,
});

const products = new Table(
  {
    business_id: column.text, name: column.text, brand: column.text, category: column.text, iva_rate: column.real, is_active: column.integer,
    image_path: column.text, catalog_visible: column.integer, description: column.text,
    unit: column.text, subcategory: column.text, margin_pct: column.real, price_rounding: column.text, ask_price: column.integer, dated: column.integer, kitchen: column.integer,
    // normal / insumo (no se vende) / fabricado (tiene receta).
    kind: column.text,
    /** Dónde se guarda (estante, depósito, heladera…). */
    storage_location: column.text,
    // 1.79.0: mostrar en Ventas / Salón, aviso de stock bajo (insumos) y costo para recetas (insumo: % y monto por unidad).
    show_pos: column.integer, show_salon: column.integer, low_stock_alert: column.integer, recipe_markup_pct: column.real, recipe_markup_amount: column.integer,
    /** 1.81.24: minutos que tarda en la cocina (lo que no es receta; las recetas lo tienen en recipes). */ prep_minutes: column.integer,
    /** 1.141.0: unidades que trae el bulto (para vender suelto) y recargo propio por vender suelto (null = el general). */
    pack_units: column.integer, loose_pct: column.real, loose_half_pct: column.real, loose_quarter_pct: column.real,
  },
  { indexes: { by_name: ["business_id", "name"] } },
);

const product_variants = new Table(
  {
    business_id: column.text, product_id: column.text, name: column.text, barcode: column.text, sku: column.text,
    price: column.integer, cost: column.integer, is_active: column.integer,
    /** 1.142.0: presentación «unidad» de un bulto (la arma el servidor): de qué presentación es y cuántas trae el bulto. */
    pack_of: column.text, pack_qty: column.integer,
  },
  { indexes: { by_barcode: ["barcode"], by_product: ["product_id"] } },
);

const stock = new Table(
  {
    business_id: column.text, variant_id: column.text, location_id: column.text, qty: column.real,
    min_stock: column.real, updated_at: column.text,
  },
  { indexes: { by_variant: ["variant_id"], by_var_loc: ["variant_id", "location_id"] } },
);

const stock_lots = new Table({
  business_id: column.text, variant_id: column.text, location_id: column.text, lot_code: column.text,
  expires_on: column.text, qty: column.real,
});

const customers = new Table(
  {
    business_id: column.text, name: column.text, doc_type: column.text, doc_number: column.text, iva_condition: column.text,
    phone: column.text, email: column.text, address: column.text, credit_limit: column.integer, balance: column.integer,
    merged_into: column.text, price_list_id: column.text, created_at: column.text,
    points: column.integer, no_reminders: column.integer, no_marketing: column.integer,
    /** 1.126.0: desactivado (no se busca en ventas), eliminado (sale de todo) y foto. */ is_active: column.integer, deleted_at: column.text, photo_path: column.text,
  },
  { indexes: { by_doc: ["doc_number"] } },
);

const current_account_movements = new Table({
  business_id: column.text, customer_id: column.text, amount: column.integer, kind: column.text, ref_type: column.text,
  ref_id: column.text, note: column.text, voided: column.integer, device_id: column.text, created_by: column.text, created_at: column.text,
  /** 1.124.0: anulado desde la ficha del cliente (motivo, quién y cuándo). */ void_reason: column.text, voided_by: column.text, voided_at: column.text,
  /** 1.151.0: seña de una reserva. */ reservation_id: column.text,
});

const cash_sessions = new Table({ voided_at: column.text, voided_by: column.text, void_reason: column.text,
  business_id: column.text, cash_register_id: column.text, device_id: column.text, opened_by: column.text, opened_at: column.text,
  opening_amount: column.integer, opening_counter: column.text, closed_by: column.text, closed_at: column.text,
  closing_counter: column.text, expected_amount: column.integer, counted_amount: column.integer, transferred_to_admin: column.integer,
  // Envío a la Caja Admin al cerrar (fajos) y correcciones.
  sent_amount: column.integer, sent_counter: column.text, sent_at: column.text, sent_by: column.text, reopened_count: column.integer, edited_at: column.text,
  // 1.78.0: bolivianos (solo el total, en centavos de Bs): apertura, contado, esperado y enviado a la Caja Admin.
  opening_bob: column.integer, closing_bob: column.integer, expected_bob: column.integer, sent_bob: column.integer,
  /** 1.89.0: cobros que llegaron después del cierre. */ late_movements: column.integer,
});

const cash_movements = new Table({
  business_id: column.text, session_id: column.text, kind: column.text, method: column.text, amount: column.integer,
  bill_counter: column.text, note: column.text, ref_type: column.text, ref_id: column.text, device_id: column.text,
  created_by: column.text, created_at: column.text,
  // Anulado o corregido desde la gestión de cierres; adelanto de sueldo: a quién.
  voided_at: column.text, voided_by: column.text, void_reason: column.text, edited_at: column.text, employee_id: column.text,
  // Adelanto a alguien del Personal.
  staff_id: column.text,
  // 1.78.0: cobro en bolivianos (method cash_bob): amount en pesos y fx_amount en Bs.
  currency: column.text, fx_amount: column.integer,
}, { indexes: { by_session: ["session_id"] } });

// Personal (lo mínimo: para elegir a quién es un adelanto en la caja).
const staff = new Table({ business_id: column.text, name: column.text, position: column.text, user_id: column.text, location_id: column.text, active: column.integer });

const sales = new Table(
  {
    business_id: column.text, location_id: column.text, device_id: column.text, cash_session_id: column.text,
    internal_number: column.text, customer_id: column.text, subtotal: column.integer, discount: column.integer,
    total: column.integer, status: column.text, invoice_status: column.text, created_by: column.text, created_at: column.text,
    reservation_id: column.text, price_list_id: column.text, note: column.text, restaurant_order_id: column.text, restaurant_partial: column.integer,
    /** 1.81.12: número de la sucursal (0003-00001234). */ branch_number: column.text,
    /** 1.85.0: lo que cobra del pedido del salón (JSON {items:[{id,qty}], cover}); la nube lo suma a los renglones. */ restaurant_paid: column.text,
    /** 1.87.0: descuento a mano (pesos y %) para el control de la nube. */ manual_discount: column.integer, manual_discount_pct: column.real,
  },
  // 1.81.22: también por sucursal, caja y cliente (listas del día, cierres y fichas de cliente).
  { indexes: { by_date: ["created_at"], by_loc_date: ["location_id", "created_at"], by_session: ["cash_session_id"], by_customer: ["customer_id"] } },
);

const sale_items = new Table({
  business_id: column.text, sale_id: column.text, variant_id: column.text, description: column.text, qty: column.real,
  unit_price: column.integer, list_price: column.integer, offer_id: column.text, total: column.integer, modifiers: column.text,
}, { indexes: { by_sale: ["sale_id"] } });

// reference: en tarjeta, la orden del posnet (ORD…) que la respalda.
const sale_payments = new Table({ business_id: column.text, sale_id: column.text, method: column.text, amount: column.integer, reference: column.text,
  // 1.78.0: pago en bolivianos.
  currency: column.text, fx_amount: column.integer, fx_rate: column.real, fx_change: column.integer }, { indexes: { by_sale: ["sale_id"] } });
/** Devoluciones (parciales o totales): items y reintegros en JSON. */
const sale_returns = new Table({
  business_id: column.text, location_id: column.text, device_id: column.text, sale_id: column.text, cash_session_id: column.text,
  items: column.text, total: column.integer, refunds: column.text, reason: column.text, replaced_by_sale_id: column.text,
  sale_created_by: column.text, created_by: column.text, created_at: column.text,
}, { indexes: { by_sale: ["sale_id"] } });
/** Categorías (parent_id null) y subcategorías. */
const product_categories = new Table({ voided_at: column.text, voided_by: column.text, void_reason: column.text, business_id: column.text, parent_id: column.text, name: column.text, created_at: column.text });

const stock_movements = new Table({
  business_id: column.text, variant_id: column.text, location_id: column.text, delta: column.real, kind: column.text,
  ref_type: column.text, ref_id: column.text, lot_code: column.text, expires_on: column.text, note: column.text,
  // 1.171.0: quien lo hizo en el equipo (con su PIN): la nube controla el permiso de Stock con esa persona, no con la de la sesión.
  device_id: column.text, created_by: column.text, created_at: column.text,
});

const remote_config = new Table({
  scope: column.text, scope_id: column.text, business_id: column.text, key: column.text, value: column.text, version: column.integer,
  apply_at: column.text,
});

const device_commands = new Table({ device_id: column.text, business_id: column.text, command: column.text, payload: column.text, status: column.text });
const arca_points_of_sale = new Table({
  business_id: column.text, number: column.integer, mode: column.text, device_id: column.text, status: column.text,
  last_number_a: column.integer, last_number_b: column.integer, last_number_c: column.integer,
  /** 1.67: domicilio del punto en ARCA (sale en las facturas de ese punto). */
  location_id: column.text, address: column.text, address_in_arca: column.integer, address_parts: column.text,
});
const invoices = new Table(
  {
    business_id: column.text, sale_id: column.text, point_of_sale: column.integer, cbte_tipo: column.integer, cbte_number: column.integer,
    auth_mode: column.text, auth_code: column.text, auth_due: column.text, customer_doc_type: column.integer, customer_doc_number: column.text,
    net: column.integer, iva: column.integer, total: column.integer, status: column.text, error: column.text, issued_at: column.text,
    created_at: column.text, kind: column.text, letter: column.text, location_id: column.text, device_id: column.text,
    customer_id: column.text, customer_name: column.text, customer_iva: column.text, items: column.text,
    associated_invoice_id: column.text, created_by: column.text,
    /** 1.90.0: motivo al facturar solo una parte de la venta. */ partial_reason: column.text,
    /** 1.63: datos del emisor cuando se emitió (si después cambió el CUIT del negocio). JSON. */
    issuer: column.text,
    /** 1.99.17: la devolución que originó la nota de crédito. */ return_id: column.text,
    /** 1.144.0: cuánto tardó cada paso del CAE (JSON, lo escribe el servidor). */ timings: column.text,
  },
  { indexes: { by_sale: ["sale_id"], by_date: ["created_at"] } },
);
const caea_periods = new Table({
  business_id: column.text, period: column.text, period_order: column.integer, caea: column.text, valid_from: column.text,
  valid_to: column.text, report_until: column.text, status: column.text,
});
const role_permissions = new Table({ business_id: column.text, role: column.text, permission: column.text, custom_role_id: column.text });
// Roles propios del negocio.
const custom_roles = new Table({ business_id: column.text, name: column.text, base_role: column.text, description: column.text, created_at: column.text });
const user_roles = new Table({ user_id: column.text, business_id: column.text, role: column.text, blocked: column.integer, display_name: column.text, custom_role_id: column.text, allowed_locations: column.text });
/** Diseños de comprobantes (Impresoras → Diseño de comprobantes). Se editan online; bajan para imprimir sin internet. */
const print_templates = new Table({
  business_id: column.text, kind: column.text, name: column.text, definition: column.text, is_active: column.integer, updated_at: column.text,
});
const user_pins = new Table({ user_id: column.text, business_id: column.text, pin_hash: column.text, pin_salt: column.text, locked_until: column.text, pin_ver: column.text });
const sync_errors = new Table({
  business_id: column.text, device_id: column.text, op_table: column.text, op_id: column.text, op_type: column.text,
  payload: column.text, error: column.text, created_at: column.text,
});
const profiles = new Table({ full_name: column.text, phone: column.text, default_business_id: column.text });

const authorizations = new Table({
  business_id: column.text, kind: column.text, requested_by: column.text, authorized_by: column.text, ref_type: column.text,
  ref_id: column.text, amount: column.integer, note: column.text, device_id: column.text, created_at: column.text,
});

const price_lists = new Table({
  business_id: column.text, name: column.text, use_for_catalog: column.integer, is_active: column.integer, created_at: column.text,
  rule_mode: column.text, rule_value: column.real, rule_rounding: column.text, min_order: column.integer,
});
const custom_prices = new Table(
  { business_id: column.text, price_list_id: column.text, variant_id: column.text, price: column.integer, updated_at: column.text, mode: column.text, value: column.real },
  { indexes: { by_list_variant: ["price_list_id", "variant_id"] } },
);
const offers = new Table({
  business_id: column.text, name: column.text, unit_price: column.integer, min_qty: column.real, location_id: column.text,
  starts_on: column.text, ends_on: column.text, is_active: column.integer, created_by: column.text, created_at: column.text,
  // Promociones (1.14.0): tipo y sus datos.
  kind: column.text, pct: column.real, buy_qty: column.integer, pay_qty: column.integer, nth: column.integer, min_amount: column.integer,
  payment_method: column.text, category: column.text, brand: column.text, gift_variant_id: column.text, gift_qty: column.real,
  time_from: column.text, time_to: column.text, weekdays: column.text, pack_price: column.integer,
});
const offer_items = new Table({ business_id: column.text, offer_id: column.text, variant_id: column.text, qty: column.real }, { indexes: { by_variant: ["variant_id"] } });
// Producción: receta por presentación fabricada, sus insumos y las órdenes.
const recipes = new Table({ voided_at: column.text, voided_by: column.text, void_reason: column.text, business_id: column.text, variant_id: column.text, yield_qty: column.real, notes: column.text, updated_at: column.text,
  waste_pct: column.real, extra_cost: column.integer, prep_minutes: column.integer, shelf_life_days: column.integer,
  ask_modifiers: column.integer, remove_pricing: column.text, add_pricing: column.text, made_to_order: column.integer }, { indexes: { by_variant: ["variant_id"] } });
const recipe_items = new Table({ business_id: column.text, recipe_id: column.text, variant_id: column.text, qty: column.real,
  removable: column.integer, mod_price: column.integer, remove_mode: column.text }, { indexes: { by_recipe: ["recipe_id"] } });
// 1.51: lo que se ofrece para agregar al vender (extra queso…).
const recipe_extras = new Table({ business_id: column.text, recipe_id: column.text, variant_id: column.text, qty: column.real, price: column.integer, add_mode: column.text, add_pct: column.real,
  max_count: column.integer, sort: column.integer }, { indexes: { by_recipe: ["recipe_id"] } });
const production_orders = new Table({
  business_id: column.text, location_id: column.text, variant_id: column.text, qty: column.real, inputs: column.text, unit_cost: column.integer,
  lot_code: column.text, expires_on: column.text, note: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  plan_id: column.text, batches: column.real, extra_cost: column.integer, adjusted: column.integer, sale_price: column.integer,
}, { indexes: { by_date: ["created_at"] } });
// Plantillas de etiquetas (diseño en JSON; una por defecto y una por producto).
const label_templates = new Table({ kind: column.text, voided_at: column.text, voided_by: column.text, void_reason: column.text, business_id: column.text, name: column.text, design: column.text, is_default: column.integer, product_id: column.text,
  created_by: column.text, created_at: column.text, updated_at: column.text });
// Órdenes de producción planificadas (planificada → en proceso → terminada) y marcas.
const production_plans = new Table({
  business_id: column.text, location_id: column.text, number: column.text, variant_id: column.text, qty: column.real, planned_for: column.text,
  assigned_to: column.text, status: column.text, note: column.text, production_order_id: column.text, device_id: column.text,
  created_by: column.text, created_at: column.text, updated_at: column.text,
}, { indexes: { by_status: ["status", "planned_for"] } });
const brands = new Table({ voided_at: column.text, voided_by: column.text, void_reason: column.text, business_id: column.text, name: column.text, is_active: column.integer, created_at: column.text });
// Direcciones de los clientes (varias por cliente).
const customer_addresses = new Table({ voided_at: column.text, voided_by: column.text, void_reason: column.text,
  business_id: column.text, customer_id: column.text, label: column.text, address: column.text, city: column.text, notes: column.text, province: column.text,
  is_default: column.integer, created_at: column.text, lat: column.real, lng: column.real,
}, { indexes: { by_customer: ["customer_id"] } });
const reservations = new Table({
  business_id: column.text, location_id: column.text, number: column.text, customer_id: column.text, customer_name: column.text,
  phone: column.text, note: column.text, status: column.text, due_on: column.text, sale_id: column.text, device_id: column.text,
  created_by: column.text, created_at: column.text, closed_at: column.text, expires_at: column.text,
});
const reservation_items = new Table(
  { business_id: column.text, reservation_id: column.text, variant_id: column.text, description: column.text, qty: column.real, unit_price: column.integer },
  { indexes: { by_reservation: ["reservation_id"], by_variant: ["variant_id"] } },
);
const quotes = new Table({
  business_id: column.text, location_id: column.text, number: column.text, customer_id: column.text, customer_name: column.text,
  items: column.text, total: column.integer, valid_until: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  expires_at: column.text, reservation_id: column.text, sale_id: column.text, sold_at: column.text,
});

// Gastos (funcionan sin internet) y sus categorías.
const expense_categories = new Table({ business_id: column.text, name: column.text, is_active: column.integer });
const expenses = new Table(
  {
    business_id: column.text, location_id: column.text, category_id: column.text, amount: column.integer, paid_from: column.text,
    method: column.text, cash_movement_id: column.text, supplier_id: column.text, doc_number: column.text, note: column.text,
    spent_on: column.text, receipt_path: column.text, voided_at: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  },
  { indexes: { by_date: ["business_id", "spent_on"] } },
);

// Envíos (el repartidor trabaja sin internet) y asistencia del personal.
const deliveries = new Table(
  {
    business_id: column.text, location_id: column.text, number: column.text, sale_id: column.text, catalog_order_id: column.text,
    customer_id: column.text, customer_name: column.text, phone: column.text, address: column.text, address_notes: column.text,
    zone: column.text, scheduled_for: column.text, time_window: column.text, driver_id: column.text, cash_to_collect: column.integer,
    status: column.text, collected: column.integer, collected_method: column.text, failed_reason: column.text, delivered_at: column.text,
    settlement_id: column.text, note: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
    trip_id: column.text, stop_seq: column.integer, lat: column.real, lng: column.real, geo_source: column.text,
    items: column.text, address_label: column.text,
    /** Paga con (efectivo, para el vuelto) y cobro de Mercado Pago del repartidor. */
    pays_with: column.integer, mp_ref: column.text, mp_ref_kind: column.text, mp_linked_at: column.text, shipping_fee: column.integer,
  },
  { indexes: { by_day: ["scheduled_for", "status"], by_driver: ["driver_id", "scheduled_for"] } },
);
const delivery_events = new Table(
  {
    business_id: column.text, delivery_id: column.text, kind: column.text, driver_id: column.text, collected: column.integer,
    collected_method: column.text, reason: column.text, scheduled_for: column.text, lat: column.real, lng: column.real, trip_id: column.text, mp_ref: column.text, mp_ref_kind: column.text,
    device_id: column.text, created_by: column.text, created_at: column.text,
  },
  { indexes: { by_delivery: ["delivery_id", "created_at"] } },
);
const delivery_settlements = new Table({
  business_id: column.text, driver_id: column.text, amount: column.integer, expected: column.integer, delivery_ids: column.text,
  cash_movement_id: column.text, note: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  trip_id: column.text, voided_at: column.text, void_reason: column.text, cash_float: column.integer,
});
/** Viajes de reparto (hoja de ruta): número del servidor, repartidor, día y estado. */
const delivery_trips = new Table({
  business_id: column.text, location_id: column.text, number: column.integer, driver_id: column.text, day: column.text, status: column.text,
  started_at: column.text, finished_at: column.text, note: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  /** Cambio que lleva el repartidor (sale de la caja al armar el viaje). */ cash_float: column.integer, cash_float_movement_id: column.text,
}, { indexes: { by_day: ["day", "driver_id"] } });
/** Costo del envío (1.47): cómo se cobra y las zonas dibujadas en el mapa (las edita un admin con internet). */
const delivery_fee_settings = new Table({
  business_id: column.text, mode: column.text, fixed: column.integer, base: column.integer, per_km: column.integer, min_fee: column.integer, max_km: column.real,
  road_factor: column.real, round_up: column.integer, outside: column.text, outside_price: column.integer, product_variant_id: column.text, updated_at: column.text,
});
const delivery_zones = new Table({
  business_id: column.text, name: column.text, price: column.integer, color: column.text, shape: column.text, sort: column.integer, created_at: column.text, updated_at: column.text,
});
const time_clock = new Table(
  {
    business_id: column.text, location_id: column.text, user_id: column.text, kind: column.text, at: column.text, method: column.text,
    note: column.text, voided_at: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  },
  { indexes: { by_user: ["user_id", "at"] } },
);
const work_schedules = new Table({ business_id: column.text, user_id: column.text, weekday: column.integer, starts: column.text, ends: column.text, location_id: column.text });

// Convenios con obras sociales (se usan en el mostrador sin internet).
const health_plans = new Table({
  business_id: column.text, name: column.text, code: column.text, channel: column.text, coverage_pct: column.real, requires_prescription: column.integer,
  requires_authorization: column.integer, validator_url: column.text, max_items_per_prescription: column.integer, is_active: column.integer,
});
const health_plan_rules = new Table({ business_id: column.text, plan_id: column.text, category: column.text, variant_id: column.text, coverage_pct: column.real });
const health_dispensations = new Table({
  business_id: column.text, sale_id: column.text, plan_id: column.text, customer_id: column.text, patient_name: column.text, affiliate_number: column.text,
  prescription_number: column.text, prescription_date: column.text, doctor_license: column.text, authorization_code: column.text, items: column.text,
  covered_total: column.integer, patient_total: column.integer, period: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
}, { indexes: { by_sale: ["sale_id"] } });

// ---- Solo locales (nunca se suben) ----
/** Contador correlativo de numeración interna por equipo. */
const local_counters = new Table({ name: column.text, value: column.integer }, { localOnly: true });
/** 1.99.19: transacciones de los puestos que el servidor local ya guardó (id = la clave del puesto). */
const local_tx_keys = new Table({ at: column.text }, { localOnly: true });
/** 1.91.0: bloques de números de venta de la sucursal reservados para este equipo (antes en localStorage). */
const local_branch_blocks = new Table(
  { location_id: column.text, branch: column.integer, next_no: column.integer, last_no: column.integer, created_at: column.text },
  { localOnly: true },
);
/** Operaciones que el servidor rechazó (copia local para mostrarlas aunque no haya internet). */
const local_rejected_ops = new Table(
  { op_table: column.text, op_id: column.text, op_type: column.text, payload: column.text, error: column.text, created_at: column.text },
  { localOnly: true },
);
/** Conteos del inventario por partes (se suben al servidor; no bajan a los equipos). */
const inventory_counts = new Table({
  business_id: column.text, location_id: column.text, variant_id: column.text, part: column.text, counted: column.real, system_qty: column.real,
  device_id: column.text, counted_by: column.text, created_at: column.text,
});
/** Canjes de puntos hechos en el equipo (se suben; los puntos de cada cliente bajan en customers.points). */
const loyalty_movements = new Table({
  business_id: column.text, customer_id: column.text, kind: column.text, points: column.integer, amount: column.integer,
  ref_type: column.text, ref_id: column.text, note: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
});
/** Registro de la sincronización: lo que se subió bien, lo rechazado, reintentos y descartes. */
const local_sync_log = new Table(
  { at: column.text, kind: column.text, op_table: column.text, op_id: column.text, op_type: column.text, count: column.integer, detail: column.text },
  { localOnly: true },
);

// Entregas de mercadería (parcial o total) de cada venta: items en JSON [{ sale_item_id, variant_id, description, qty }].
const sale_deliveries = new Table({
  business_id: column.text, location_id: column.text, sale_id: column.text, items: column.text, note: column.text,
  device_id: column.text, created_by: column.text, sale_created_by: column.text, created_at: column.text,
  /** 1.160.0: entrega en contra (revierte esta entrega; cantidades en negativo). */
  reverts_id: column.text,
}, { indexes: { by_sale: ["sale_id"] } });

// Local de Mercado Pago de cada sucursal (si existe, se puede cobrar con QR dinámico).
const mp_stores = new Table({ business_id: column.text, location_id: column.text, name: column.text });

// Posnets Point vinculados a la app (para cobrar con tarjeta).
const mp_terminals = new Table({
  business_id: column.text, location_id: column.text, nickname: column.text, mp_pos_id: column.text, operating_mode: column.text, is_active: column.integer,
});

// Cobros recibidos en la cuenta de Mercado Pago del negocio (los baja el servidor; el equipo solo imputa).
const mp_payments = new Table(
  {
    business_id: column.text, mp_id: column.integer, kind: column.text, status: column.text, amount: column.integer, approved_at: column.text,
    payer_name: column.text, payer_doc: column.text, description: column.text,
    sale_id: column.text, account_movement_id: column.text, customer_id: column.text, dismissed: column.integer, note: column.text,
    matched_at: column.text, matched_by: column.text,
  },
  { indexes: { by_date: ["approved_at"], by_sale: ["sale_id"] } },
);

// Modo restaurante (1.48): plano del salón, pedidos por mesa, comandas a la cocina y atajos por usuario.
const restaurant_areas = new Table({ business_id: column.text, location_id: column.text, name: column.text, sort: column.integer, is_active: column.integer, created_at: column.text, updated_at: column.text });
const restaurant_tables = new Table({
  business_id: column.text, location_id: column.text, area_id: column.text, name: column.text, shape: column.text,
  x: column.real, y: column.real, w: column.real, h: column.real, rotation: column.integer, seats: column.integer, is_active: column.integer, per_seat: column.integer,
  created_at: column.text, updated_at: column.text,
}, { indexes: { by_area: ["area_id"] } });
const restaurant_floor_objects = new Table({
  business_id: column.text, location_id: column.text, area_id: column.text, kind: column.text, label: column.text,
  x: column.real, y: column.real, w: column.real, h: column.real, rotation: column.integer, created_at: column.text, updated_at: column.text,
}, { indexes: { by_area: ["area_id"] } });
const kitchen_stations = new Table({
  business_id: column.text, location_id: column.text, name: column.text, categories: column.text, is_default: column.integer,
  sort: column.integer, is_active: column.integer, created_at: column.text, updated_at: column.text,
});
const restaurant_orders = new Table({
  business_id: column.text, location_id: column.text, number: column.text, kind: column.text, table_id: column.text, status: column.text,
  waiter_id: column.text, guests: column.integer, customer_id: column.text, customer_name: column.text, note: column.text, sale_id: column.text,
  merged_into: column.text, cancel_reason: column.text, opened_at: column.text, closed_at: column.text, device_id: column.text,
  created_by: column.text, created_at: column.text, updated_at: column.text, cover_paid: column.integer, reservation_id: column.text, group_id: column.text, group_single: column.integer,
  customer_address_id: column.text, delivery_address: column.text, delivery_notes: column.text, phone: column.text, with_delivery: column.integer, seat: column.integer,
}, { indexes: { by_status: ["status"], by_table: ["table_id"] } });
const restaurant_order_items = new Table({
  business_id: column.text, order_id: column.text, variant_id: column.text, description: column.text, qty: column.real, unit_price: column.integer,
  note: column.text, course: column.integer, station_id: column.text, status: column.text, sent_at: column.text, void_reason: column.text,
  waste: column.integer, voided_by: column.text, voided_at: column.text, created_by: column.text, created_at: column.text, updated_at: column.text,
  paid_qty: column.real, modifiers: column.text,
  /** 1.81.9: la cocina lo marcó listo y el mozo, servido. */ ready_at: column.text, ready_by: column.text, served_at: column.text, served_by: column.text,
  /** 1.81.17: de qué comensal es (1, 2, 3…; null = compartido). */ diner: column.integer,
  /** 1.81.24: la cocina lo aceptó (en cocción) y el tiempo estimado al mandarlo. */ started_at: column.text, started_by: column.text, prep_minutes: column.integer,
  /** 1.82.1: el mozo preguntó «¿cuánto falta?» y la demora que avisó la cocina. */ asked_at: column.text, extra_minutes: column.integer,
}, { indexes: { by_order: ["order_id"] } });
const kitchen_tickets = new Table({
  business_id: column.text, location_id: column.text, order_id: column.text, station_id: column.text, number: column.text, kind: column.text,
  course: column.integer, table_name: column.text, waiter_name: column.text, lines: column.text, device_id: column.text,
  created_by: column.text, created_at: column.text,
  /** 1.81.14: el equipo que la mandó no tiene impresora para ese sector: la imprime otra PC del local. */ remote_print: column.integer,
  /** 1.99.20: los renglones que lleva (JSON). */ item_ids: column.text,
}, { indexes: { by_order: ["order_id"] } });
const restaurant_shortcuts = new Table({ business_id: column.text, user_id: column.text, variant_ids: column.text, updated_at: column.text });
// 1.49: cubierto, turnos con su pozo de propinas y reservas de mesas.
const restaurant_settings = new Table({ business_id: column.text, cover_enabled: column.integer, cover_variant_id: column.text, tip_pcts: column.text, updated_at: column.text,
  card_design: column.text, wifi_name: column.text, wifi_pass: column.text, instagram: column.text, slogan: column.text,
  courses_enabled: column.integer, qr_menu: column.integer, qr_pay: column.integer, menu_categories: column.text,
  waiter_own_cash: column.integer, waiter_methods: column.text });
// 1.50: pedidos y pagos desde el QR de la mesa.
const restaurant_order_requests = new Table({
  business_id: column.text, location_id: column.text, table_id: column.text, items: column.text, total: column.integer, note: column.text, status: column.text,
  order_id: column.text, decided_at: column.text, decided_by: column.text, reject_reason: column.text, created_at: column.text,
  /** 1.60: la silla (barra con cuenta por silla). */ seat: column.integer,
}, { indexes: { by_table: ["table_id"] } });
const restaurant_qr_payments = new Table({
  business_id: column.text, location_id: column.text, table_id: column.text, order_id: column.text, amount: column.integer, tip: column.integer, total: column.integer,
  status: column.text, mp_id: column.integer, paid: column.integer, approved_at: column.text, sale_id: column.text, closed_by: column.text, created_at: column.text, updated_at: column.text,
}, { indexes: { by_table: ["table_id"] } });
const restaurant_calls = new Table({ business_id: column.text, location_id: column.text, table_id: column.text, kind: column.text, created_at: column.text, attended_at: column.text, attended_by: column.text });
const restaurant_shifts = new Table({
  business_id: column.text, location_id: column.text, status: column.text, opened_at: column.text, opened_by: column.text, closed_at: column.text, closed_by: column.text,
  tips_total: column.integer, split: column.text, note: column.text, device_id: column.text, created_at: column.text, updated_at: column.text,
});
const restaurant_tips = new Table({
  business_id: column.text, location_id: column.text, shift_id: column.text, order_id: column.text, sale_id: column.text, amount: column.integer, method: column.text,
  waiter_id: column.text, cash_movement_id: column.text, voided_at: column.text, void_reason: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
  /** 1.162.0: el cobro de Mercado Pago que trajo la propina (si fue por transferencia o QR en el cobro de la mesa). */ mp_payment_id: column.text,
}, { indexes: { by_shift: ["shift_id"] } });
/** 1.80.0: la caja que recibe habilita al mozo a cobrar las mesas. */
const waiter_grants = new Table({
  business_id: column.text, location_id: column.text, session_id: column.text, waiter_id: column.text, granted_by: column.text,
  revoked_at: column.text, revoked_by: column.text, device_id: column.text, created_at: column.text, order_id: column.text,
});
/** 1.80.0: lo que el mozo cobró en efectivo (y sus propinas en efectivo) y tiene que rendir en la caja. */
const waiter_handovers = new Table({
  business_id: column.text, location_id: column.text, session_id: column.text, waiter_id: column.text, sale_id: column.text, order_id: column.text, label: column.text,
  cash_amount: column.integer, bob_amount: column.integer, tip_amount: column.integer, status: column.text, settled_at: column.text, settled_by: column.text,
  device_id: column.text, created_by: column.text, created_at: column.text,
}, { indexes: { by_status: ["status"] } });
/** 1.80.0: propinas entregadas a cada mozo (egreso de la caja «Propinas para empleados»). tip_ids: JSON con las propinas. */
const restaurant_tip_payouts = new Table({
  business_id: column.text, location_id: column.text, waiter_id: column.text, amount: column.integer, tip_ids: column.text, session_id: column.text,
  cash_movement_id: column.text, device_id: column.text, created_by: column.text, created_at: column.text,
});
const restaurant_reservations = new Table({
  business_id: column.text, location_id: column.text, day: column.text, time: column.text, guests: column.integer, name: column.text, phone: column.text, note: column.text,
  table_id: column.text, customer_id: column.text, status: column.text, order_id: column.text, device_id: column.text, created_by: column.text, created_at: column.text, updated_at: column.text,
}, { indexes: { by_day: ["day", "time"] } });

// WhatsApp por API: configuración (sin claves) y bandeja de salida (los últimos 7 días).
const whatsapp_settings = new Table({
  business_id: column.text, enabled: column.integer, source: column.text, status: column.text, phone: column.text,
  events: column.text, templates: column.text, reminder_days: column.integer, reminder_min_balance: column.integer, last_error: column.text,
});
/** 1.156.0: cheques recibidos en cartera (se cargan al cobrar, sin internet; los cambios de estado son online). */
const cheques = new Table({
  business_id: column.text, direction: column.text, kind: column.text, bank: column.text, branch: column.text, number: column.text, amount: column.integer,
  issue_date: column.text, due_date: column.text, drawer_name: column.text, drawer_cuit: column.text, crossed: column.integer, not_to_order: column.integer,
  customer_id: column.text, status: column.text, image_path: column.text, note: column.text, device_id: column.text, created_by: column.text, created_at: column.text
});
const whatsapp_outbox = new Table({
  business_id: column.text, to_phone: column.text, body: column.text, kind: column.text, ref_id: column.text, customer_id: column.text,
  status: column.text, attempts: column.integer, error: column.text, device_id: column.text, created_by: column.text, created_at: column.text, sent_at: column.text,
}, { indexes: { by_date: ["created_at"] } });

/** 1.163.0: la apariencia que eligió cada usuario (paleta, claro/oscuro y los colores de la propia). La última por updated_at. */
const user_ui_prefs = new Table({
  business_id: column.text, user_id: column.text, palette: column.text, mode: column.text, custom: column.text, device_id: column.text, updated_at: column.text, created_at: column.text,
}, { indexes: { by_user: ["business_id", "user_id"] } });
const TABLES = {
  businesses, locations, cash_registers, devices, products, product_variants, stock, stock_lots, customers,
  current_account_movements, cash_sessions, cash_movements, sales, sale_items, sale_payments, stock_movements,
  remote_config, device_commands, arca_points_of_sale, caea_periods, role_permissions, user_roles, user_pins,
  sync_errors, profiles, authorizations, price_lists, custom_prices, offers, offer_items, reservations, reservation_items,
  quotes, invoices, expense_categories, expenses, deliveries, delivery_events, delivery_settlements, time_clock, work_schedules,
  health_plans, health_plan_rules, health_dispensations, print_templates, sale_returns, product_categories, local_counters, local_tx_keys, local_branch_blocks, local_rejected_ops, local_sync_log, inventory_counts, loyalty_movements, mp_payments, mp_terminals, mp_stores, sale_deliveries,
  whatsapp_settings, whatsapp_outbox, custom_roles, customer_addresses, staff, delivery_trips, delivery_fee_settings, delivery_zones, recipes, recipe_items, recipe_extras, production_orders, production_plans, brands, label_templates,
  restaurant_areas, restaurant_tables, restaurant_floor_objects, kitchen_stations, restaurant_orders, restaurant_order_items, kitchen_tickets, restaurant_shortcuts,
  restaurant_settings, restaurant_shifts, restaurant_tips, restaurant_reservations, restaurant_calls, restaurant_order_requests, restaurant_qr_payments,
  waiter_grants, waiter_handovers, restaurant_tip_payouts, cheques, user_ui_prefs,
};

export const AppSchema = new Schema(TABLES);

/** 1.81.0: nombres de las tablas (el puesto sabe qué volver a leer cuando el servidor avisa un cambio). */
export const TABLE_NAMES: string[] = Object.keys(TABLES);

export type Database = (typeof AppSchema)["types"];

/**
 * Tablas de solo agregado: se suben con INSERT ... ON CONFLICT DO NOTHING (reenviar es inofensivo).
 * El resto se sube como upsert / update / delete normal.
 */
export const APPEND_ONLY_TABLES = new Set([
  "sales", "sale_items", "sale_payments", "stock_movements", "cash_movements", "current_account_movements", "authorizations", "inventory_counts", "loyalty_movements",
  "delivery_events", "delivery_settlements", "health_dispensations", "sale_returns", "sale_deliveries", "whatsapp_outbox", "production_orders", "kitchen_tickets", "restaurant_tips",
  "restaurant_tip_payouts", "cheques",
]);

/** Tablas que el equipo puede escribir. Cualquier otra escritura local se descarta al subir. */
/** Altas que se reenvían sin pisar (ON CONFLICT DO NOTHING) pero admiten un cambio de estado (PATCH). */
export const IDEMPOTENT_PUT_TABLES = new Set([...APPEND_ONLY_TABLES, "quotes", "invoices", "expenses", "time_clock", "customer_addresses", "delivery_trips", "restaurant_orders", "restaurant_order_items", "restaurant_shifts", "waiter_grants", "waiter_handovers"]);

export const WRITABLE_TABLES = new Set([
  ...APPEND_ONLY_TABLES, "quotes", "customers", "cash_sessions", "device_commands", "stock", "products", "product_variants",
  "price_lists", "custom_prices", "offers", "offer_items", "reservations", "reservation_items", "invoices", "expenses", "deliveries", "delivery_trips", "time_clock",
  "product_categories", "mp_payments", "customer_addresses", "recipes", "recipe_items", "recipe_extras", "production_plans", "brands", "label_templates",
  "restaurant_areas", "restaurant_tables", "restaurant_floor_objects", "kitchen_stations", "restaurant_orders", "restaurant_order_items", "restaurant_shortcuts",
  "restaurant_settings", "restaurant_reservations", "restaurant_shifts", "restaurant_calls", "restaurant_order_requests", "restaurant_qr_payments",
  "waiter_grants", "waiter_handovers", "restaurant_tip_payouts", "user_ui_prefs",
]);
